import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import useDebouncedValue from "../hooks/useDebouncedValue";
import { buildModuleRegistrationPayload } from "./moduleRegistrationPayload";

export default function ModuleRegistration() {
  const auth = useAuth();
  const apiUrl = window.APP_CONFIG?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  // Step 1: Subdetector
  const [subdetector, setSubdetector] = useState("");

  // Step 2: Module type
  const [moduleType, setModuleType] = useState("");

  // Step 3: HDI selection
  const [hdiSearchText, setHdiSearchText] = useState("");
  const debouncedHdiSearchText = useDebouncedValue(hdiSearchText);
  const [hdiOptions, setHdiOptions] = useState([]);
  const [selectedHdi, setSelectedHdi] = useState("");
  const [hdiDetails, setHdiDetails] = useState(null);
  const [loadingHdis, setLoadingHdis] = useState(false);
  const [loadingHdiDetails, setLoadingHdiDetails] = useState(false); // New state for HDI details loading

  // Step 4: TBPX layer selection (only for layer 3+4)
  const [tbpxLayer, setTbpxLayer] = useState("");

  // Step 5: Bare module selection
  const [bareModuleSearchText, setBareModuleSearchText] = useState("");
  const [bareModuleOptions, setBareModuleOptions] = useState([]);
  const [selectedBareModule, setSelectedBareModule] = useState("");
  const [loadingBareModules, setLoadingBareModules] = useState(false);

  // New: per-slot state for 3D modules (slot 0 and slot 1)
  const [bareModuleSearchTextSlot0, setBareModuleSearchTextSlot0] = useState("");
  const [bareModuleOptionsSlot0, setBareModuleOptionsSlot0] = useState([]);
  const [selectedBareModuleSlot0, setSelectedBareModuleSlot0] = useState("");
  const [loadingBareModulesSlot0, setLoadingBareModulesSlot0] = useState(false);

  const [bareModuleSearchTextSlot1, setBareModuleSearchTextSlot1] = useState("");
  const [bareModuleOptionsSlot1, setBareModuleOptionsSlot1] = useState([]);
  const [selectedBareModuleSlot1, setSelectedBareModuleSlot1] = useState("");
  const [loadingBareModulesSlot1, setLoadingBareModulesSlot1] = useState(false);

  const [error, setError] = useState("");

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // --- New states for metadata and JSON generation ---
  const [shortName, setShortName] = useState("");
  const [shortNameError, setShortNameError] = useState("");
  const [shortNameChecking, setShortNameChecking] = useState(false);
  // Use production DB by default.
  const [useDev, setUseDev] = useState(false);
  const [gluingDate, setGluingDate] = useState("");
  const [gluingLocation, setGluingLocation] = useState("");
  const [wirebondingDate, setWirebondingDate] = useState("");
  const [wirebondingLocation, setWirebondingLocation] = useState("");
  const [coatingDate, setCoatingDate] = useState("");
  const [coatingLocation, setCoatingLocation] = useState("");
  const [location, setLocation] = useState("");
  const [comments, setComments] = useState("");
  const [mechanicalStatus, setMechanicalStatus] = useState("");
  // default status to "Untested"
  const [statusField, setStatusField] = useState("Untested");
  const [isRotated, setIsRotated] = useState("");
  const [fieldOptions, setFieldOptions] = useState(null);
  const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Module type options based on subdetector
  const getModuleTypeOptions = () => {
    if (subdetector === "TEPX") return ["IT Quad Module"];
    if (subdetector === "TFPX") return ["IT Quad Module", "IT Double Module"];
    if (subdetector === "TBPX") return ["IT Quad Module", "IT Double Module", "IT 3D Module"];
    return [];
  };

  // Get HDI name filter based on subdetector and module type
  const getHdiNameFilter = () => {
    if (subdetector === "TEPX") return "ITHDI-E";
    if (subdetector === "TFPX") {
      if (moduleType === "IT Double Module") return "ITHDI-F12";
      if (moduleType === "IT Quad Module") return "ITHDI-F34";
    }
    if (subdetector === "TBPX") {
      if (moduleType === "IT 3D Module") return "ITHDI-B1";
      if (moduleType === "IT Double Module") return "ITHDI-B2";
      if (moduleType === "IT Quad Module") return "ITHDI-B34";
    }
    return "";
  };
  const hdiNameFilter = getHdiNameFilter();

  const fetchHdiOptions = useCallback(
    async (searchText, signal) => {
      if (!auth.isAuthenticated) return;

      setLoadingHdis(true);
      setError("");

      try {
        const searchFilter = searchText || hdiNameFilter;
        const url = `${apiUrl}/api/v1/parts/hdi?unattached=true&nlf=${encodeURIComponent(
          searchFilter
        )}&dev=${useDev ? "true" : "false"}`;
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${auth.user?.access_token}`,
          },
          credentials: "include",
          signal,
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch HDIs: ${res.status}`);
        }

        const data = await res.json();
        if (signal.aborted) return;
        // Filter client-side to ensure we only show HDIs matching the exact prefix.
        setHdiOptions(data.filter((hdi) => hdi.startsWith(hdiNameFilter)));
      } catch (err) {
        if (signal.aborted || err.name === "AbortError") return;
        setError(err.message || "Failed to load HDI options");
        setHdiOptions([]);
      } finally {
        if (!signal.aborted) setLoadingHdis(false);
      }
    },
    [apiUrl, auth, hdiNameFilter, useDev]
  );

  // Get bare module type based on module type
  const getBareModuleType = () => {
    if (moduleType === "IT Quad Module") return "qbmod";
    if (moduleType === "IT Double Module") return "dbmod";
    // IMPORTANT: backend expects short_name '3dbmod' for 3D bare modules so return that
    if (moduleType === "IT 3D Module") return "3dbmod";
    return "";
  };

  const fetchFieldOptions = async () => {
    if (!auth.isAuthenticated || !moduleType) return;
    setLoadingFieldOptions(true);
    setFieldOptions(null);
    try {
      const url = `${apiUrl}/api/v1/modules/field-options?dev=${
        useDev ? "true" : "false"
      }&kind_of_part=${encodeURIComponent(moduleType)}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${auth.user?.access_token}` },
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.detail || `Failed to load field options: ${res.status}`);
      }
      setFieldOptions(data);
      if (!(data.statuses || []).includes(statusField)) {
        setStatusField((data.statuses || [])[0] || "");
      }
    } catch (err) {
      setError(err.message || "Failed to load field options");
      setFieldOptions(null);
    } finally {
      setLoadingFieldOptions(false);
    }
  };

  // Calculate module name from HDI name
  const getModuleName = () => {
    if (!selectedHdi) return "";

    let moduleName = selectedHdi.replace("ITHDI", "ITMOD");

    // For TBPX layer 3+4, allow layer selection
    if (
      subdetector === "TBPX" &&
      moduleType === "IT Quad Module" &&
      selectedHdi.includes("-B34-")
    ) {
      if (tbpxLayer === "3") {
        moduleName = moduleName.replace("-B34-", "-B3-");
      } else if (tbpxLayer === "4") {
        moduleName = moduleName.replace("-B34-", "-B4-");
      }
    }

    return moduleName;
  };

  // Check if TBPX layer selection is needed
  const needsTbpxLayerSelection = () => {
    return (
      subdetector === "TBPX" &&
      moduleType === "IT Quad Module" &&
      selectedHdi &&
      selectedHdi.includes("-B34-")
    );
  };

  // Reset dependent states when subdetector changes
  useEffect(() => {
    // auto-select IT Quad Module for TEPX (only option)
    if (subdetector === "TEPX") {
      setModuleType("IT Quad Module");
    } else {
      setModuleType("");
    }
    setSelectedHdi("");
    setHdiSearchText("");
    setHdiOptions([]);
    setHdiDetails(null);
    setTbpxLayer("");
    setSelectedBareModule("");
    setBareModuleOptions([]);
    setError("");
    setFieldErrors({});
    setFieldOptions(null);
    // clear short name and its error when switching subdetector
    setShortName("");
    setShortNameError("");
    // clear derived location when subdetector changes
    setLocation("");
  }, [subdetector]);

  // Reset HDI selection when module type changes
  useEffect(() => {
    setSelectedHdi("");
    setHdiSearchText("");
    setHdiOptions([]);
    setHdiDetails(null);
    setTbpxLayer("");
    setSelectedBareModule("");
    setBareModuleSearchText("");
    setBareModuleOptions([]);
    setError("");
    setFieldErrors({});
    setGluingLocation("");
    setWirebondingLocation("");
    setCoatingLocation("");
    setMechanicalStatus("");
    setStatusField("Untested");
    setIsRotated("");
    // clear derived location when module type changes
    setLocation("");
  }, [moduleType]);

  useEffect(() => {
    if (moduleType) fetchFieldOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleType, useDev]);

  // Reset layer and bare module when HDI changes
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setTbpxLayer("");
    setSelectedBareModule("");
    setBareModuleSearchText("");
    setBareModuleOptions([]);
    setLoadingHdiDetails(true); // Indicate loading instead

    if (selectedHdi) {
      fetchHdiDetails();
    } else {
      setLoadingHdiDetails(false); // Reset if no HDI selected
    }
  }, [selectedHdi]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Fetch HDI details and bare modules when layer is selected (if needed)
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (needsTbpxLayerSelection() && tbpxLayer) {
      fetchBareModules();
    }
  }, [tbpxLayer]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // NEW: Add dedicated effect hook for bare modules that runs whenever hdiDetails changes
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!hdiDetails) {
      // clear derived location and bare-module UI state when no details
      setLocation("");
      setSelectedBareModule("");
      setSelectedBareModuleSlot0("");
      setSelectedBareModuleSlot1("");
      setBareModuleOptions([]);
      setBareModuleOptionsSlot0([]);
      setBareModuleOptionsSlot1([]);
      return;
    }

    // The compatible-components endpoint supplies the canonical database location.
    setLocation("");

    // Clear previous selections
    setSelectedBareModule("");
    setSelectedBareModuleSlot0("");
    setSelectedBareModuleSlot1("");
    setBareModuleOptions([]);
    setBareModuleOptionsSlot0([]);
    setBareModuleOptionsSlot1([]);

    // Only fetch automatically when TBPX layer selection is not required.
    if (!needsTbpxLayerSelection()) {
      if (moduleType === "IT 3D Module") {
        // fetch both slot lists
        fetchBareModules(); // will fetch both when slot === null for 3D
      } else {
        fetchBareModules(); // existing single-list behavior
      }
    }
  }, [hdiDetails]); // This ensures it runs whenever hdiDetails is updated
  /* eslint-enable react-hooks/exhaustive-deps */

  // Fetch HDI options after search typing settles, or immediately when context changes.
  useEffect(() => {
    if (!moduleType) {
      setLoadingHdis(false);
      setHdiOptions([]);
      return undefined;
    }

    if (hdiSearchText !== debouncedHdiSearchText) {
      setLoadingHdis(true);
      return undefined;
    }

    const controller = new AbortController();
    fetchHdiOptions(debouncedHdiSearchText, controller.signal);

    return () => controller.abort();
  }, [hdiSearchText, debouncedHdiSearchText, moduleType, useDev, fetchHdiOptions]);

  // Ensure date/location pairs are consistent: either both empty or both set
  const pairIsValid = (date, loc) => {
    return (!date && !loc) || (date && loc);
  };

  const anyPairInvalid = () => {
    // gluing is mandatory but still must be consistent; wirebonding/coating optional but must be set together
    return (
      !pairIsValid(gluingDate, gluingLocation) ||
      !pairIsValid(wirebondingDate, wirebondingLocation) ||
      !pairIsValid(coatingDate, coatingLocation)
    );
  };

  // helper: get display/location value from hdiDetails
  const getHdiLocation = () => {
    return location || "N/A";
  };

  const fetchHdiDetails = async () => {
    if (!auth.isAuthenticated || !selectedHdi) return;

    setError("");
    setLoadingHdiDetails(true); // Ensure loading is set

    try {
      const url = `${apiUrl}/api/v1/parts/hdi/${encodeURIComponent(selectedHdi)}?dev=${
        useDev ? "true" : "false"
      }`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${auth.user?.access_token}`,
        },
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch HDI details: ${res.status}`);
      }

      const data = await res.json();
      setHdiDetails(data);
      setLoadingHdiDetails(false); // Loading complete

      // don't call fetchBareModules here - handled by hdiDetails effect
    } catch (err) {
      setError(err.message || "Failed to load HDI details");
      setHdiDetails(null);
      setLoadingHdiDetails(false); // Loading complete on error
    }
  };

  const fetchBareModules = async () => {
    const is3D = moduleType === "IT 3D Module";
    if (!auth.isAuthenticated || !selectedHdi || !moduleType) return;

    setLoadingBareModules(true);
    setLoadingBareModulesSlot0(is3D);
    setLoadingBareModulesSlot1(is3D);
    setError("");

    try {
      const url = `${apiUrl}/api/v1/modules/compatible-baremodules?hdi=${encodeURIComponent(
        selectedHdi
      )}&kind_of_part=${encodeURIComponent(moduleType)}&dev=${useDev ? "true" : "false"}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${auth.user?.access_token}` },
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.detail?.message || `Failed to fetch bare modules: ${res.status}`);
      }
      setLocation(data?.location?.name || "");
      if (is3D) {
        setBareModuleOptionsSlot0(data?.slots?.["0"] || []);
        setBareModuleOptionsSlot1(data?.slots?.["1"] || []);
      } else {
        setBareModuleOptions(data?.baremodules || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load bare modules");
      setLocation("");
      setBareModuleOptions([]);
      setBareModuleOptionsSlot0([]);
      setBareModuleOptionsSlot1([]);
    } finally {
      setLoadingBareModules(false);
      setLoadingBareModulesSlot0(false);
      setLoadingBareModulesSlot1(false);
    }
  };

  // Get filtered bare module options based on search text
  const getFilteredBareModules = (options, searchText) => {
    if (!searchText) return options || [];
    const lowerSearch = searchText.toLowerCase();
    return (options || []).filter((bm) => bm.toLowerCase().includes(lowerSearch));
  };

  // Ensure TEPX short_name formatting (auto-insert dash after initial if user types P1000 -> P-1000)
  const normalizeShortNameForTepx = (s) => {
    if (!s) return s;
    if (subdetector !== "TEPX") return s;
    // If pattern Letter + digits (or rest), insert dash: P1000 -> P-1000, p1000 -> p-1000
    const m = String(s).match(/^([A-Za-z])[-]?(.+)$/);
    if (m) {
      // uppercase the letter and keep the rest as entered
      return `${m[1].toUpperCase()}-${m[2]}`;
    }
    return s;
  };

  // Validate short name when TEPX: format X-0000 where X uppercase letter and 4 digits
  const validateShortName = (s) => {
    if (subdetector !== "TEPX") {
      setShortNameError("");
      return true;
    }
    if (!s) {
      setShortNameError("Short name is required for TEPX and must be in form X-0000");
      return false;
    }
    const ok = /^[A-Z]-\d{4}$/.test(s);
    setShortNameError(ok ? "" : "TEPX short name must be in form X-0000 (e.g. P-1000)");
    return ok;
  };

  const checkShortNameUniqueness = async (sn) => {
    if (!auth.isAuthenticated || !sn) return;
    if (subdetector === "TEPX" && !/^[A-Z]-\d{4}$/.test(sn)) return;

    setShortNameChecking(true);
    setShortNameError("");

    try {
      const url = `${apiUrl}/api/v1/modules/short-name-availability?value=${encodeURIComponent(
        sn
      )}&dev=${useDev ? "true" : "false"}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${auth.user?.access_token}` },
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setShortNameError(data?.detail?.message || "Could not verify short-name availability");
        return;
      }
      setShortNameError(data?.available ? "" : "Short name already exists");
    } catch {
      setShortNameError("Could not verify short-name availability");
    } finally {
      setShortNameChecking(false);
    }
  };

  // Normalize on blur to avoid surprising the user while typing
  const handleShortNameBlur = () => {
    // compute normalized from the current input value synchronously,
    // update state and run validation + uniqueness check using that value
    const normalized = normalizeShortNameForTepx(shortName || "");
    setShortName(normalized);
    const valid = validateShortName(normalized);
    // only check uniqueness when validation passes for TEPX (or for non-TEPX always)
    if (subdetector !== "TEPX" || valid) {
      checkShortNameUniqueness(normalized);
    }
  };

  const hasBareModuleSelection =
    moduleType === "IT 3D Module"
      ? Boolean(selectedBareModuleSlot0 && selectedBareModuleSlot1)
      : Boolean(selectedBareModule);

  const getBareModuleValue = () => {
    if (moduleType === "IT 3D Module") {
      return `${selectedBareModuleSlot0},${selectedBareModuleSlot1}`;
    }
    return selectedBareModule;
  };

  const getBaremoduleIdentifiers = () =>
    moduleType === "IT 3D Module"
      ? [selectedBareModuleSlot0, selectedBareModuleSlot1]
      : [selectedBareModule];

  const buildRegistrationPayload = () => {
    return buildModuleRegistrationPayload({
      selectedHdi,
      baremodules: getBaremoduleIdentifiers(),
      moduleType,
      tbpxLayer,
      requiresLayer: needsTbpxLayerSelection(),
      shortName,
      gluingDate,
      gluingLocation,
      wirebondingDate,
      wirebondingLocation,
      coatingDate,
      coatingLocation,
      mechanicalStatus,
      status: statusField,
      rotation: isRotated,
      comments,
    });
  };

  // determine whether all required fields are present and valid
  const canRegister = (() => {
    const name_label = getModuleName();
    const requiredPresent = !!(
      name_label &&
      shortName &&
      !shortNameError &&
      !shortNameChecking &&
      moduleType &&
      gluingDate &&
      gluingLocation &&
      (location || getHdiLocation()) &&
      hasBareModuleSelection &&
      mechanicalStatus &&
      fieldOptions &&
      !loadingFieldOptions
    );
    const pairsOk = !anyPairInvalid();
    return requiredPresent && pairsOk;
  })();

  const handleRegister = async () => {
    setError("");
    setFieldErrors({});
    setUploadResult(null);
    if (!canRegister) {
      setError("Cannot register: missing or invalid fields.");
      return;
    }

    setUploading(true);
    try {
      const res = await fetch(
        `${apiUrl}/api/v1/modules-registration?dev=${useDev ? "true" : "false"}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.user?.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(buildRegistrationPayload()),
          credentials: "include",
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const detail = data?.detail;
        setFieldErrors(detail?.fields || {});
        throw new Error(
          detail?.message ||
            (typeof detail === "string" ? detail : `Registration failed: ${res.status}`)
        );
      }
      setUploadResult(data || { message: "Registration succeeded" });
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Add: reset handler to return to initial subdetector selection state
  const handleReset = () => {
    // reset top-level selection and return to production DB
    setSubdetector("");
    setUseDev(false);

    // clear errors / upload
    setError("");
    setUploadResult(null);

    // clear short name / checks
    setShortName("");
    setShortNameError("");
    setShortNameChecking(false);
    setFieldErrors({});
    setFieldOptions(null);

    // clear HDI / bare module related state
    setHdiSearchText("");
    setHdiOptions([]);
    setSelectedHdi("");
    setHdiDetails(null);
    setTbpxLayer("");
    setBareModuleSearchText("");
    setBareModuleOptions([]);
    setSelectedBareModule("");
    setBareModuleSearchTextSlot0("");
    setBareModuleOptionsSlot0([]);
    setSelectedBareModuleSlot0("");
    setBareModuleSearchTextSlot1("");
    setBareModuleOptionsSlot1([]);
    setSelectedBareModuleSlot1("");

    // clear metadata fields
    setGluingDate("");
    setGluingLocation("");
    setWirebondingDate("");
    setWirebondingLocation("");
    setCoatingDate("");
    setCoatingLocation("");
    setLocation("");
    setComments("");
    setMechanicalStatus("");
    setStatusField("Untested");
    setIsRotated("");

    // clear loading/upload flags
    setUploading(false);
    setLoadingHdis(false);
    setLoadingHdiDetails(false);
    setLoadingBareModules(false);
    setLoadingBareModulesSlot0(false);
    setLoadingBareModulesSlot1(false);
  };

  const renderFieldError = (field) =>
    fieldErrors[field]?.length ? (
      <p style={{ color: "red", marginTop: 6 }}>{fieldErrors[field].join(" ")}</p>
    ) : null;

  if (!auth.isAuthenticated) {
    return (
      <div style={{ padding: 20, maxWidth: 800, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h2>Module Registration</h2>
          <a href="/help#module-registration">Help</a>
        </div>
        <p>You must be logged in to register modules.</p>
        <button
          onClick={() => {
            const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            sessionStorage.setItem("post_login_redirect", returnTo);
            auth.signinRedirect({ state: { returnTo } });
          }}
        >
          Log in
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 800, margin: "0 auto", textAlign: "left" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2>Module Registration</h2>
        <a href="/help#module-registration">Help</a>
      </div>

      {error && <p style={{ color: "red", marginBottom: 16 }}>{error}</p>}
      {fieldErrors.request?.length > 0 && (
        <p style={{ color: "red", marginBottom: 16 }}>{fieldErrors.request.join(" ")}</p>
      )}

      {/* Step 1: Subdetector selection */}
      <div style={{ marginBottom: 20 }}>
        <h3>1. Select Subdetector</h3>
        {["TBPX", "TFPX", "TEPX"].map((sd) => (
          <label key={sd} style={{ display: "block", marginBottom: 8 }}>
            <input
              type="radio"
              name="subdetector"
              value={sd}
              checked={subdetector === sd}
              onChange={(e) => setSubdetector(e.target.value)}
              style={{ marginRight: 8 }}
            />
            {sd}
          </label>
        ))}

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={useDev}
              onChange={(e) => setUseDev(e.target.checked)}
              disabled={!!subdetector} /* disable once a subdetector selected */
              title={
                subdetector
                  ? "Database selection locked after choosing subdetector. Use Reset to change."
                  : "Use development database (dev)"
              }
              style={{ marginRight: 8 }}
            />
            Use development database (dev)
          </label>

          {/* Show reset when a subdetector or other selection exists so the user can start over */}
          {subdetector && (
            <button
              onClick={handleReset}
              style={{ padding: "6px 10px", cursor: "pointer" }}
              title="Reset and return to subdetector selection"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Step 2: Module type selection */}
      {subdetector && (
        <div style={{ marginBottom: 20 }}>
          <h3>2. Select Module Type</h3>
          {getModuleTypeOptions().map((mt) => (
            <label key={mt} style={{ display: "block", marginBottom: 8 }}>
              <input
                type="radio"
                name="moduleType"
                value={mt}
                checked={moduleType === mt}
                onChange={(e) => setModuleType(e.target.value)}
                style={{ marginRight: 8 }}
              />
              {mt}
            </label>
          ))}
        </div>
      )}

      {/* Step 3: HDI selection */}
      {moduleType && (
        <div style={{ marginBottom: 20 }}>
          <h3>3. Select HDI</h3>
          <p style={{ fontSize: "0.9em", color: "#666" }}>
            HDIs starting with: <strong>{hdiNameFilter}</strong>
          </p>
          <input
            type="text"
            placeholder="Search HDI..."
            value={hdiSearchText}
            onChange={(e) => setHdiSearchText(e.target.value)}
            style={{ width: "100%", padding: 8, marginBottom: 8 }}
          />
          {loadingHdis && <p>Loading HDIs...</p>}
          {!loadingHdis && hdiOptions.length > 0 && (
            <select
              aria-label="HDI"
              value={selectedHdi}
              onChange={(e) => setSelectedHdi(e.target.value)}
              style={{ width: "100%", padding: 8 }}
              size={Math.min(hdiOptions.length + 1, 10)}
            >
              <option value="">-- Select an HDI --</option>
              {hdiOptions.map((hdi) => (
                <option key={hdi} value={hdi}>
                  {hdi}
                </option>
              ))}
            </select>
          )}
          {!loadingHdis && hdiOptions.length === 0 && (
            <p style={{ color: "#666" }}>No unattached HDIs found</p>
          )}
          {renderFieldError("hdi")}
          {renderFieldError("components")}
        </div>
      )}

      {/* Step 4: Module name and location */}
      {selectedHdi && (hdiDetails || loadingHdiDetails) && (
        <div style={{ marginBottom: 20 }}>
          <h3>4. Module Information</h3>
          {loadingHdiDetails ? (
            <p>Loading HDI details...</p>
          ) : (
            <>
              <p>
                <strong>HDI Location:</strong> {getHdiLocation()}
              </p>

              {needsTbpxLayerSelection() && (
                <div style={{ marginTop: 12 }}>
                  <p>
                    <strong>Select Layer:</strong>
                  </p>
                  {["3", "4"].map((layer) => (
                    <label key={layer} style={{ display: "block", marginBottom: 8 }}>
                      <input
                        type="radio"
                        name="tbpxLayer"
                        value={layer}
                        checked={tbpxLayer === layer}
                        onChange={(e) => setTbpxLayer(e.target.value)}
                        style={{ marginRight: 8 }}
                      />
                      Layer {layer}
                    </label>
                  ))}
                  {renderFieldError("layer")}
                </div>
              )}

              {(!needsTbpxLayerSelection() || tbpxLayer) && (
                <p style={{ marginTop: 12 }}>
                  <strong>Module Name:</strong>{" "}
                  <span style={{ color: "#0066cc", fontSize: "1.1em" }}>{getModuleName()}</span>
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Step 5: Bare module selection */}
      {selectedHdi && hdiDetails && (!needsTbpxLayerSelection() || tbpxLayer) && (
        <div style={{ marginBottom: 20 }}>
          <h3>5. Select Bare Module</h3>
          <p style={{ fontSize: "0.9em", color: "#666" }}>
            Showing unattached {getBareModuleType()} in location: {getHdiLocation()}
          </p>
          {/* If building a 3D module we need two bare modules (slot 0 and slot 1) */}
          {moduleType === "IT 3D Module" ? (
            <>
              {(loadingBareModulesSlot0 || loadingBareModulesSlot1) && (
                <p>Loading bare modules for both slots...</p>
              )}

              <div style={{ marginTop: 8 }}>
                <label>
                  <strong>Bare module (slot 0)</strong>
                </label>
                <input
                  type="text"
                  placeholder="Search slot 0..."
                  value={bareModuleSearchTextSlot0}
                  onChange={(e) => setBareModuleSearchTextSlot0(e.target.value)}
                  style={{ width: "100%", padding: 8, marginBottom: 8 }}
                />
                <select
                  aria-label="Bare module slot 0"
                  value={selectedBareModuleSlot0}
                  onChange={(e) => setSelectedBareModuleSlot0(e.target.value)}
                  style={{ width: "100%", padding: 8 }}
                  size={Math.min(
                    getFilteredBareModules(bareModuleOptionsSlot0, bareModuleSearchTextSlot0)
                      .length + 1,
                    10
                  )}
                >
                  <option value="">-- Select a bare module for slot 0 --</option>
                  {getFilteredBareModules(bareModuleOptionsSlot0, bareModuleSearchTextSlot0).map(
                    (bm) => (
                      <option key={bm} value={bm}>
                        {bm}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div style={{ marginTop: 12 }}>
                <label>
                  <strong>Bare module (slot 1)</strong>
                </label>
                <input
                  type="text"
                  placeholder="Search slot 1..."
                  value={bareModuleSearchTextSlot1}
                  onChange={(e) => setBareModuleSearchTextSlot1(e.target.value)}
                  style={{ width: "100%", padding: 8, marginBottom: 8 }}
                />
                <select
                  aria-label="Bare module slot 1"
                  value={selectedBareModuleSlot1}
                  onChange={(e) => setSelectedBareModuleSlot1(e.target.value)}
                  style={{ width: "100%", padding: 8 }}
                  size={Math.min(
                    getFilteredBareModules(bareModuleOptionsSlot1, bareModuleSearchTextSlot1)
                      .length + 1,
                    10
                  )}
                >
                  <option value="">-- Select a bare module for slot 1 --</option>
                  {getFilteredBareModules(bareModuleOptionsSlot1, bareModuleSearchTextSlot1).map(
                    (bm) => (
                      <option key={bm} value={bm}>
                        {bm}
                      </option>
                    )
                  )}
                </select>
              </div>
            </>
          ) : (
            <>
              {loadingBareModules && <p>Loading bare modules...</p>}
              {!loadingBareModules && bareModuleOptions.length > 0 && (
                <>
                  <input
                    type="text"
                    placeholder="Search bare module..."
                    value={bareModuleSearchText}
                    onChange={(e) => setBareModuleSearchText(e.target.value)}
                    style={{ width: "100%", padding: 8, marginBottom: 8 }}
                  />
                  <select
                    aria-label="Bare module"
                    value={selectedBareModule}
                    onChange={(e) => setSelectedBareModule(e.target.value)}
                    style={{ width: "100%", padding: 8 }}
                    size={Math.min(
                      getFilteredBareModules(bareModuleOptions, bareModuleSearchText).length + 1,
                      10
                    )}
                  >
                    <option value="">-- Select a bare module --</option>
                    {getFilteredBareModules(bareModuleOptions, bareModuleSearchText).map((bm) => (
                      <option key={bm} value={bm}>
                        {bm}
                      </option>
                    ))}
                  </select>
                  {bareModuleSearchText &&
                    getFilteredBareModules(bareModuleOptions, bareModuleSearchText).length ===
                      0 && (
                      <p style={{ color: "#666", marginTop: 8 }}>
                        No bare modules match your search
                      </p>
                    )}
                </>
              )}
              {!loadingBareModules && bareModuleOptions.length === 0 && (
                <p style={{ color: "orange" }}>No unattached bare modules found in this location</p>
              )}
            </>
          )}
          {renderFieldError("baremodules")}
        </div>
      )}

      {/* Step 6: Module metadata (new) - only show after a bare module is selected */}
      {selectedHdi &&
        hdiDetails &&
        (moduleType === "IT 3D Module"
          ? selectedBareModuleSlot0 && selectedBareModuleSlot1
          : selectedBareModule) &&
        (!needsTbpxLayerSelection() || tbpxLayer) && (
          <div style={{ marginBottom: 20 }}>
            <h3>6. Module Metadata (for JSON)</h3>

            {/* name_label: computed module name (no override allowed) */}
            <p style={{ marginTop: 8 }}>
              <strong>Name label (computed):</strong>{" "}
              <span style={{ color: "#0066cc" }}>{getModuleName() || "(not available yet)"}</span>
            </p>

            {/* short_name */}
            <label style={{ display: "block", marginTop: 12 }}>
              <span style={{ fontSize: "0.9em" }}>
                <strong>Short name</strong>
              </span>
              <input
                type="text"
                placeholder={
                  subdetector === "TEPX" ? "e.g. P-1000 (TEPX requires dash)" : "e.g. P1000"
                }
                value={shortName}
                onChange={(e) => {
                  setShortName(e.target.value);
                  if (shortNameError) setShortNameError("");
                }}
                onBlur={handleShortNameBlur}
                style={{ width: "100%", padding: 8, marginTop: 6 }}
              />
              {shortNameError && <p style={{ color: "red", marginTop: 6 }}>{shortNameError}</p>}
              {shortNameChecking && <p style={{ color: "#666", marginTop: 6 }}>Checking...</p>}
              {renderFieldError("short_name")}
            </label>

            {/* kind_of_part is moduleType (already selected) */}
            <p style={{ marginTop: 12 }}>
              <strong>Kind of part:</strong> {moduleType}
            </p>

            {/* gluing_date and gluing_location */}
            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: "0.9em" }}>Gluing date</span>
                <input
                  type="date"
                  value={gluingDate}
                  onChange={(e) => setGluingDate(e.target.value)}
                  style={{ width: "100%", padding: 8, marginTop: 6 }}
                />
                {renderFieldError("gluing_date")}
              </label>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: "0.9em" }}>Gluing location</span>
                <select
                  value={gluingLocation}
                  onChange={(e) => setGluingLocation(e.target.value)}
                  style={{ width: "100%", padding: 8, marginTop: 6 }}
                >
                  <option value="">-- select gluing location --</option>
                  {(fieldOptions?.gluingLocations || []).map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
                {renderFieldError("gluing_location")}
              </label>
            </div>

            {/* wirebonding date + wirebonding location (manufacturers list) */}
            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: "0.9em" }}>Wirebonding date</span>
                <input
                  type="date"
                  value={wirebondingDate}
                  onChange={(e) => setWirebondingDate(e.target.value)}
                  style={{ width: "100%", padding: 8, marginTop: 6 }}
                />
                {renderFieldError("wirebonding_date")}
              </label>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: "0.9em" }}>Wire-bonding location (manufacturer)</span>
                <select
                  value={wirebondingLocation}
                  onChange={(e) => setWirebondingLocation(e.target.value)}
                  style={{ width: "100%", padding: 8, marginTop: 6 }}
                >
                  <option value="">-- select wire-bonding location --</option>
                  {(fieldOptions?.wirebondingLocations || []).map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
                {renderFieldError("wirebonding_location")}
              </label>
            </div>

            {/* coating date + coating location (free text, example SCS allowed) */}
            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: "0.9em" }}>Coating date</span>
                <input
                  type="date"
                  value={coatingDate}
                  onChange={(e) => setCoatingDate(e.target.value)}
                  style={{ width: "100%", padding: 8, marginTop: 6 }}
                />
                {renderFieldError("coating_date")}
              </label>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: "0.9em" }}>Coating location (select)</span>
                <select
                  value={coatingLocation}
                  onChange={(e) => setCoatingLocation(e.target.value)}
                  style={{ width: "100%", padding: 8, marginTop: 6 }}
                >
                  <option value="">-- none --</option>
                  {(fieldOptions?.coatingLocations || []).map((coatingOption) => (
                    <option key={coatingOption} value={coatingOption}>
                      {coatingOption}
                    </option>
                  ))}
                </select>
                {renderFieldError("coating_location")}
              </label>
            </div>
            {/* pair validation errors for coating */}
            {!pairIsValid(coatingDate, coatingLocation) && (
              <p style={{ color: "red", marginTop: 6 }}>
                Both coating date and coating location must be set together.
              </p>
            )}

            {/* module location (from allowed list) */}
            <p style={{ marginTop: 12 }}>
              <strong>Module location:</strong> {location || getHdiLocation()}
            </p>

            {/* pair validation errors for gluing and wirebonding (show near metadata section) */}
            {!pairIsValid(gluingDate, gluingLocation) && (
              <p style={{ color: "red", marginTop: 6 }}>
                Both gluing date and gluing location must be set together.
              </p>
            )}
            {!pairIsValid(wirebondingDate, wirebondingLocation) && (
              <p style={{ color: "red", marginTop: 6 }}>
                Both wirebonding date and wire-bonding location must be set together.
              </p>
            )}
            {/* baremodule is selectedBareModule (already selected) */}
            <p style={{ marginTop: 12 }}>
              <strong>Bare module:</strong>{" "}
              {moduleType === "IT 3D Module"
                ? `${selectedBareModuleSlot0 || "(not selected)"},${
                    selectedBareModuleSlot1 || "(not selected)"
                  }`
                : selectedBareModule || "(not selected)"}
            </p>

            {/* mechanical_status (dropdown) */}
            <label style={{ display: "block", marginTop: 12 }}>
              <span style={{ fontSize: "0.9em" }}>Mechanical status</span>
              <select
                value={mechanicalStatus}
                onChange={(e) => setMechanicalStatus(e.target.value)}
                style={{ width: "100%", padding: 8, marginTop: 6 }}
              >
                <option value="">-- select mechanical status --</option>
                {(fieldOptions?.mechanicalStatuses || []).map((ms) => (
                  <option key={ms} value={ms}>
                    {ms}
                  </option>
                ))}
              </select>
              {renderFieldError("mechanical_status")}
            </label>

            {/* status (dropdown) */}
            <label style={{ display: "block", marginTop: 12 }}>
              <span style={{ fontSize: "0.9em" }}>Status</span>
              <select
                value={statusField}
                onChange={(e) => setStatusField(e.target.value)}
                style={{ width: "100%", padding: 8, marginTop: 6 }}
              >
                {(fieldOptions?.statuses || []).map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
              {renderFieldError("status")}
            </label>

            {/* is_rotated */}
            <label style={{ display: "block", marginTop: 12 }}>
              <span style={{ fontSize: "0.9em" }}>Is rotated</span>
              <select
                value={isRotated}
                onChange={(e) => setIsRotated(e.target.value)}
                style={{ width: "100%", padding: 8, marginTop: 6 }}
              >
                {(fieldOptions?.rotationOptions || []).map((rotationOption) => {
                  const value = rotationOption.value === null ? "" : String(rotationOption.value);
                  return (
                    <option key={rotationOption.label} value={value}>
                      {rotationOption.label}
                    </option>
                  );
                })}
              </select>
              {renderFieldError("is_rotated")}
            </label>

            {/* comments */}
            <label style={{ display: "block", marginTop: 12 }}>
              <span style={{ fontSize: "0.9em" }}>Comments</span>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                maxLength={4000}
                rows={3}
                style={{ width: "100%", padding: 8, marginTop: 6 }}
              />
              {renderFieldError("comments")}
            </label>
          </div>
        )}

      {/* Registration button */}
      {selectedHdi && hasBareModuleSelection && (
        <div style={{ marginTop: 30, padding: 20, background: "#f0f0f0", borderRadius: 4 }}>
          <h3>Ready to Register</h3>
          <p>
            <strong>Module:</strong> {getModuleName()}
          </p>
          <p>
            <strong>HDI:</strong> {selectedHdi}
          </p>
          <p>
            <strong>Bare Module:</strong> {getBareModuleValue()}
          </p>
          <button
            onClick={handleRegister}
            disabled={!canRegister || uploading}
            style={{
              padding: "10px 20px",
              cursor: !canRegister || uploading ? "not-allowed" : "pointer",
              opacity: !canRegister || uploading ? 0.6 : 1,
            }}
          >
            {uploading ? "Uploading..." : "Register Module"}
          </button>
          {uploadResult && (
            <pre style={{ marginTop: 12, background: "#fff", padding: 8 }}>
              {JSON.stringify(uploadResult, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* JSON preview: only display after bare module selection is complete */}
      {hasBareModuleSelection && (
        <div style={{ marginTop: 20 }}>
          <h3>Generated JSON</h3>
          <p style={{ fontSize: "0.9em", color: "#666" }}>
            This is the request sent to the registration API. The server adds the canonical module
            name and location.
          </p>
          {(() => {
            const item = buildRegistrationPayload();

            return (
              <>
                {/* Basic validation hint for required fields */}
                {(!selectedHdi ||
                  !shortName ||
                  shortNameError ||
                  !moduleType ||
                  !gluingDate ||
                  !gluingLocation ||
                  !(location || getHdiLocation()) ||
                  !hasBareModuleSelection ||
                  !mechanicalStatus) && (
                  <p style={{ color: "orange" }}>
                    Note: some mandatory fields are missing or invalid. Fix them to finalize the
                    request.
                  </p>
                )}
                {/* Additional validation: date/location pairs must be consistent */}
                {anyPairInvalid() && (
                  <p style={{ color: "orange" }}>
                    Date/location pairs must be set together. Fill or clear the corresponding date
                    or location fields for gluing, wirebonding and coating.
                  </p>
                )}

                <pre
                  style={{ background: "#f6f8fa", padding: 12, borderRadius: 4, overflowX: "auto" }}
                >
                  {JSON.stringify(item, null, 2)}
                </pre>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
